import { formatCurrency, formatMessage } from "lib/globalize-util";
import { List, Map as ImmutableMap, Record } from "immutable";
import _ from "lodash";
import fp from "lodash/fp";
import moment from "moment";
import get from "lodash/get";
import set from "lodash/set";
import allPass from "lodash/fp/allPass";
import pipe from "lodash/fp/pipe";
import propEq from "lodash/fp/propEq";
import querystring, { stringify } from "querystring";
import fetch from "lib/fetch";
import me from "models/me";
import {
    IMAGE,
    IMAGE_360,
    VIDEO,
    THREEDIUM,
    MEDIA_TYPE_360,
    MEDIA_TYPE_IMAGE,
    MEDIA_TYPE_VIDEO,
    MEDIA_TYPE_THREEDIUM,
} from "constants/media";
import { createSizeRecord, ISizeRecord } from "./size";
import type { IScope } from "reducers/scope";
import { IProductMediaAsset } from "constants/assets";
import { IMediaType, IProductMedia, IMediaImage } from "constants/product-media";
import config from "site-config";
import { getSwatchValue } from "helpers/swatch-utils";
import { ISwatch } from "constants/color-swatches";
import { IThreediumModel, IPlanar2dModel } from "../../constants/product-customizations/customization";
import { isProductCustomizationEnabled } from "components/customization-modal/helpers";

const defaults = () => ({
    __active: false,
    __archived: false,
    __available_from: undefined,
    __available_until: undefined,
    __banners: [],
    __cancelled: false,
    __color: undefined,
    __colorBanner: undefined,
    __created_on: undefined,
    __images: [],
    __media: [],
    __inventory: [],
    __modified_on: undefined,
    __out_of_range: undefined, // used by catalog service to indicate item isn't available in the catalog delivery window
    __price_templates: [],
    __prices: [],
    __pricesheets: [],
    __pricing: ImmutableMap,
    promotions: undefined,
    __schema_id: undefined,
    __size_groups: [],
    __sizes: List,
    __sold_out: false,
    __tracks_inventory: false,
    __unique_key: "",
    __variants: [],
    __warehouse_ids: [],
    _id: undefined,
    __images_cache: [],
    __exempt_from_order_surcharge: false,
    __pack_ids: [],
    __product_customizations_template_id: "",
    __product_customizations_threedium_model: undefined,
    __product_customizations_planar2d_model: undefined,
});

export interface IBaseItemFields extends Record<any, any> {
    __active: boolean;
    __archived: boolean;
    __available_from: moment.Moment;
    __available_until: moment.Moment;
    __banners: any;
    __cancelled: boolean;
    __color: string;
    __colorBanner: any;
    __created_on: moment.Moment;
    __images: string[];
    __inventory: string[];
    __modified_on: moment.Moment;
    __price_templates: IPriceTemplate[];
    __prices: any;
    __pricesheets: any;
    __pricing: ImmutableMap<string, any>;
    __schema_id: string;
    __size_groups: any;
    promotions?: any;
    __sizes: List<any>;
    __sold_out: boolean;
    __tracks_inventory: boolean;
    __unique_key: string;
    __variants: IProductVariant[];
    __warehouse_ids: string[];
    _id: string;
    __out_of_range: boolean;
    __media: IProductMediaAsset[];
    __exempt_from_order_surcharge: boolean;
    __pack_ids: string[];
    __product_customizations_template_id?: string;
    __product_customizations_threedium_model?: IThreediumModel;
    __product_customizations_planar2d_model?: IPlanar2dModel;
    proof_of_product_id?: string;
}

export interface IItemRecord extends IBaseItemFields {
    [fdmField: string]: any;
    getImageUrl: (scope: IScope, size?: ImageSize, index?: number) => string;
    countMediaImages: (scope: IScope) => number;
    getImagesAndSwatch: (scope: IScope, size: ImageSize) => string[];
    getMedia: (scope: IScope, size?: ImageSize) => IProductMedia[];
    getMediaUpdate: (type: IMediaType, assetId?: string) => string[];
    getPricesheetSizePrices: (currencyCode: string, scope: IScope, type: string, effectiveDate?: Date) => IPriceLookup;
    getSizeGroups: () => Array<{ name: string; sizes: ISizeRecord[] }>;
}

interface IProductBase {
    style_number: string;
    season: string;
    color: string;
    name: string;
}

export interface IVariant {
    ref: string;
    color: string;
    color_swatch?: ISwatch;
    swatch_label?: string;
    display_name?: string;
}

export interface IProductVariant {
    key: "color";
    items: IVariant[];
    hasMore: boolean;
}

export enum ProductBannerType {
    DEFAULT = "default",
    CUSTOMIZATION = "customization",
}

export interface IBanner {
    name: string;
    foreground_color: string;
    background_color: string;
    type?: boolean;
}

export type IProduct = (IProductBase & IBaseItemFields) | IItemRecord;

// used for mapping GO media type to product one
export const MEDIA_MAP: { [key in IMAGE | VIDEO | IMAGE_360 | THREEDIUM]: 1 | 2 | 3 | 4 } = {
    [MEDIA_TYPE_IMAGE]: 1,
    [MEDIA_TYPE_VIDEO]: 2,
    [MEDIA_TYPE_360]: 3,
    [MEDIA_TYPE_THREEDIUM]: 4,
};

export type ImageSize = "T" | "M" | "G" | "W" | "";

export const createRecord = (schema) => {
    const fdmDefaults = defaults();

    schema.fields.forEach((field) => {
        fdmDefaults[field.key] = field.details.type === "multi" ? [] : undefined;
    });

    class ItemRecord extends Record(fdmDefaults) {
        static schema = schema;

        declare __active: boolean;
        declare __archived: boolean;
        declare __available_from: moment.Moment;
        declare __available_until: moment.Moment;
        declare __banners: any;
        declare __cancelled: boolean;
        declare __color: string;
        declare __colorBanner: any;
        declare __created_on: moment.Moment;
        declare __images: string[];
        declare __inventory: string[];
        declare __modified_on: moment.Moment;
        declare __price_templates: IPriceTemplate[];
        declare __pricesheets: any;
        declare __prices: any;
        declare __pricing: ImmutableMap<string, any>;
        declare __schema_id: string;
        declare __size_groups: any;
        declare __sizes: List<any>;
        declare __sold_out: boolean;
        declare __tracks_inventory: boolean;
        declare __unique_key: string;
        declare __variants: IProductVariant[];
        declare __warehouse_ids: string[];
        declare _id: string;
        declare __out_of_range: boolean;
        declare __images_cache: string[];
        declare __media: IProductMediaAsset[];
        declare __exempt_from_order_surcharge: boolean;
        declare __pack_ids: string[];
        declare __product_customizations_template_id: string;
        declare __product_customizations_threedium_model: IThreediumModel;
        declare __product_customizations_planar2d_model: IPlanar2dModel;
        declare proof_of_product_id: string;

        declare color: string; // required FDM field for images and swatches
        declare promotions: any;

        private cdn: string;

        private templateIndexes?: any;

        private pricesheetIndexes?: any;

        constructor(args: any = {}) {
            const Size = createSizeRecord(schema);

            super({
                ...args,
                __available_from: moment.utc(args.__available_from),
                __available_until: args.__available_until ? moment.utc(args.__available_until) : undefined,
                __pricing: ImmutableMap(args.__pricing && args.__pricing.map((val) => [val.__currency_code, val])),
                __sizes: List(
                    args.__sizes &&
                        args.__sizes.map((val) => {
                            return new Size(val);
                        }),
                ),
            });

            this.cdn = formatCdn(args._id);
        }

        get _disabled(): boolean {
            const priceTemplate = this.getPriceTemplate();

            if (priceTemplate) {
                return priceTemplate.__disabled;
            }

            if (me.isGuestBuyer()) {
                return false;
            }

            const pricingEntry = this.__pricing.get(me._activeCurrency);

            return !pricingEntry || pricingEntry.__disabled;
        }

        isCustomizable(scope: IScope): boolean {
            return (
                isProductCustomizationEnabled(scope.brand, scope.company) && !!this.__product_customizations_template_id
            );
        }

        getCustomizationBannerData(scope: IScope): IBanner | null {
            const brandBanners = scope?.brand?.modules?.base?.product_banners || [];
            return brandBanners.find((banner) => banner.type === ProductBannerType.CUSTOMIZATION);
        }

        hasCustomizationBanner(scope: IScope): boolean {
            const banner = this.getCustomizationBannerData(scope);
            return this.isCustomizable(scope) && !!banner;
        }

        getCustomizationBanner(scope: IScope) {
            const customizationBanner = this.getCustomizationBannerData(scope);
            if (!customizationBanner) {
                return null;
            }

            const { background_color, foreground_color, name } = customizationBanner;

            return {
                bottom: true,
                background_color,
                foreground_color,
                name: name || formatMessage("CUSTOMIZABLE"),
            };
        }

        getLink(scope: IScope, queryParam?: { [key: string]: string | undefined }) {
            return `/brand/${scope.brand._id}/item/${this._id}${queryParam ? `?${stringify(queryParam)}` : ""}`;
        }

        /**
         * Returns product prices.
         * @return {{ msrp: string, original_wholesale: string, adjusted_wholesale:string, discounted_wholesale: string  }} all prices related to product.
         */
        getFormattedPrices(currency: string, options: { msrpCurrency: string; date: Date }, scope: IScope) {
            const msrpCurrency = options.msrpCurrency || me._activeMSRPCurrency;
            const retailPrice = this.formattedMSRPRange(msrpCurrency, scope, options.date);
            const originalWholesale = this.getOriginalWholesalePrices(currency, scope, options.date);
            const minOriginalWholesale = _.min(Object.values(originalWholesale || {}));
            const adjustedWholesale = this.getAdjustedWholesalePrices(currency, scope, options.date);
            const minAdjustedWholesale = _.min(Object.values(adjustedWholesale));
            const discountedWholesale = this.getDiscountedWholesalePrices(currency, scope, options.date);
            const minDiscountedWholesale = _.min(Object.values(discountedWholesale));
            // formatted prices will be with currency and price range if price per size enabled
            const formattedOriginalWholesale = this.formatPrice(originalWholesale, currency);
            const formattedWholesale = this.formatPriceRange(currency, scope, options.date);
            const formattedAdjustedWholesale = this.formatPrice(adjustedWholesale, currency);
            // Validation for cases when wholesale is greater than adjusted_wholesale
            return {
                msrp: !scope.hideMsrp && retailPrice,
                minOriginalWholesale: minOriginalWholesale,
                originalWholesale: !scope.hideWholesale && formattedOriginalWholesale,
                minAdjustedWholesale: minAdjustedWholesale,
                adjustedWholesale: !scope.hideWholesale && (formattedAdjustedWholesale || formattedWholesale),
                minDiscountedWholesale: minDiscountedWholesale,
                discountedWholesale: !scope.hideWholesale && this.formatPrice(discountedWholesale, currency),
            };
        }

        formatPriceForAnalytics(scope, currency = me._activeCurrency) {
            const priceObject = this.getWholesalePrices(currency, scope);
            const priceArray = Object.values(priceObject);
            return Math.min(...priceArray) || 0;
        }

        formatPriceRange(currency, scope, effectiveDate?: Date) {
            const wholesalePricing = this.getWholesalePrices(currency, scope, effectiveDate);
            return this.formatPrice(wholesalePricing, currency);
        }

        formattedMSRPRange(currency, scope, effectiveDate?: Date) {
            const retailPricing = this.getRetailPrices(currency, scope, effectiveDate);
            return this.formatPrice(retailPricing, currency);
        }

        formatOriginalWholesalePrice(currency, scope, effectiveDate?: Date) {
            const price = this.getOriginalWholesalePrices(currency, scope, effectiveDate);
            return this.formatPrice(price, currency);
        }

        formatAdjustedWholesalePrice(currency: string, scope: IScope, effectiveDate?: Date): string {
            const price = this.getAdjustedWholesalePrices(currency, scope, effectiveDate);
            return this.formatPrice(price, currency);
        }

        formatDiscountedWholesalePrice(currency: string, scope: IScope, effectiveDate?: Date): string {
            const price = this.getDiscountedWholesalePrices(currency, scope, effectiveDate);
            return this.formatPrice(price, currency);
        }

        hasPriceRange(currency: string, scope: IScope, effectiveDate?: Date) {
            const wholesalePrices = Object.values(this.getWholesalePrices(currency, scope, effectiveDate)).filter(
                Boolean,
            );
            const adjustedWholesalePrices = Object.values(
                this.getAdjustedWholesalePrices(currency, scope, effectiveDate),
            ).filter(Boolean);
            const hasWholesaleRange =
                !!wholesalePrices.length && Math.min(...wholesalePrices) !== Math.max(...wholesalePrices);
            const hasAdjustedWholesaleRange =
                !!adjustedWholesalePrices.length &&
                Math.min(...adjustedWholesalePrices) !== Math.max(...adjustedWholesalePrices);
            return hasWholesaleRange || hasAdjustedWholesaleRange;
        }

        /**
         * Returns a URL of the product image on the index if exists.
         * @param {IScope} scope - nuorder scope.
         * @param {ImageSize} size - images size.
         * @param {number} index - index of an image to get URL for. Default to 0.
         * @return {string | undefined} image URL.
         */
        getImageUrl(scope: IScope, size: ImageSize = "", index: number = 0): string | undefined {
            return _.get(this.getMediaImages(scope, size), [index, "source"]);
        }

        getImagesAndSwatch(scope: IScope, size: ImageSize = ""): string[] {
            return _.map([...this.getMediaImages(scope, size), ...this.getMediaSwatches(scope)], "source");
        }

        getMediaFallback(scope: IScope): IMediaImage[] {
            const fallbackUrl = getFallbackImage(scope);
            return fallbackUrl
                ? [
                      {
                          type: MEDIA_TYPE_IMAGE as IMAGE,
                          source: fallbackUrl,
                      },
                  ]
                : [];
        }

        getMediaImages(scope: IScope, size: ImageSize = ""): IMediaImage[] {
            const images = scope.brand?.getPreference?.("unlimited_images")
                ? (_.filter(this.__media, { type: MEDIA_MAP[MEDIA_TYPE_IMAGE] }).map(
                      parseMedia(scope, size),
                  ) as IMediaImage[])
                : _.map(this.__images, (image) => ({
                      type: MEDIA_TYPE_IMAGE as IMAGE,
                      source: image.startsWith("http")
                          ? image
                          : `${this.cdn}/product/${image}${cdnImageSize(size)}.jpg`,
                      download: !scope.brand.hasPreference("restrict_image_download")
                          ? `${config.api}/api/v3.0/item/image/${image}`
                          : null,
                  }));
            return !_.isEmpty(images) ? images : this.getMediaFallback(scope);
        }

        countMediaImages(scope: IScope): number {
            const useUnlimitedImages = scope.brand?.getPreference?.("unlimited_images");
            return _.size(
                useUnlimitedImages ? _.filter(this.__media, { type: MEDIA_MAP[MEDIA_TYPE_IMAGE] }) : this.__images,
            );
        }

        getMediaSwatches(scope: IScope): IMediaImage[] {
            return _(scope.brand.modules.base.color_swatches)
                .filter({ color: getSwatchValue(this, scope), swatch_type: "file" })
                .map(({ value }) => ({
                    type: MEDIA_TYPE_IMAGE as IMAGE,
                    source: `//cdn1.nuorder.com/brand_color_swatch/${value}.jpg`,
                    download: !scope.brand.hasPreference("restrict_image_download")
                        ? `${config.api}/api/v3.0/brand_color_swatch/${value}`
                        : null,
                }))
                .value();
        }

        /**
         * Return list of all product media including video an 360 content
         * @param {IScope} scope - nuorder scope.
         * @param {ImageSize} size - images size.
         * @return {IProductMedia[]} media list.
         */
        getMedia(scope: IScope, size: ImageSize = ""): IProductMedia[] {
            if (scope.brand?.getPreference?.("unlimited_images")) {
                const use3dAssets = scope.brand?.getPreference?.("use_3d_assets");
                const media = _.map(this.__media, parseMedia(scope, size)).filter(({ type }) =>
                    use3dAssets ? true : type !== MEDIA_TYPE_THREEDIUM,
                );
                return [
                    ...media,
                    ...(_.some(media, { type: MEDIA_TYPE_IMAGE }) ? [] : this.getMediaFallback(scope)),
                    ...this.getMediaSwatches(scope),
                ];
            } else {
                return [
                    ...this.getMediaImages(scope, size),
                    ...this.getMediaSwatches(scope),
                    ..._.filter(this.__media, { type: MEDIA_MAP[MEDIA_TYPE_VIDEO] }).map(parseMedia(scope, size)),
                    ..._.filter(this.__media, { type: MEDIA_MAP[MEDIA_TYPE_360] }).map(parseMedia(scope, size)),
                ];
            }
        }

        /**
         * Return a new list of media asset ids for media update.
         * @param {IMediaType} type - media type to update.
         * @param {string} assetId - a new asset id to replace by.
         * @return {string} list of assets.
         */
        getMediaUpdate(type: IMediaType, assetId?: string): string[] {
            return _(this.__media).reject({ type: MEDIA_MAP[type] }).map("asset_id").concat(assetId).compact().value();
        }

        getBanner(scope) {
            let banner;
            const brandBanners = scope.brand.modules.base.product_banners || [];
            if (scope.brand._id && brandBanners.length && this.__banners.length) {
                banner = brandBanners.find((b) => b._id === this.__banners[0]);
            }
            return banner;
        }

        isAvailable(scope) {
            if (!scope.brand._id) {
                return false;
            }
            if (scope.brand.hasPreference("filter_soldout_products") && this.__sold_out) {
                return false;
            }
            if (scope.brand.hasPreference("filter_products_without_inventory") && !this.__tracks_inventory) {
                return false;
            }
            if (
                scope.brand.hasPreference("enforce_available_until") &&
                this.__available_until &&
                this.__available_until.isBefore(moment.utc())
            ) {
                return false;
            }

            return this.__active && !this.__archived;
        }

        formatPrice(priceLookup: IPriceLookup, currency = me._activeCurrency) {
            const prices = Object.values(priceLookup);
            const maxPrice = Math.max(...prices);
            const minPrice = Math.min(...prices);

            if (!prices.length || !minPrice || !maxPrice) {
                return "";
            }

            return minPrice === maxPrice
                ? formatCurrency(minPrice, currency)
                : `${formatCurrency(minPrice, currency)} - ${formatCurrency(maxPrice, currency)}`;
        }

        getPriceTemplate(): IPriceTemplate {
            this.templateIndexes ??= this.__price_templates
                ? _.groupBy(this.__price_templates, "__template")
                : undefined;
            return this.templateIndexes?.[me._activePriceTemplate]?.[0];
        }

        getPricesheet(currencyCode?: string) {
            if (this.pricesheetIndexes === undefined && this.__pricesheets) {
                this.pricesheetIndexes = _.groupBy(this.__pricesheets, "__name");
            }
            if (currencyCode) {
                return this.pricesheetIndexes
                    ? _.find(this.pricesheetIndexes[me._activePriceTemplate], { __currency_code: currencyCode }) ||
                          _.head(this.pricesheetIndexes[me._activePriceTemplate])
                    : undefined;
            }
            return this.pricesheetIndexes ? _.head(this.pricesheetIndexes[me._activePriceTemplate]) : undefined;
        }

        getRetailPrices(currencyCode: string, scope: IScope, effectiveDate?: Date): IPriceLookup {
            return this.getSizePrices(currencyCode, scope, "retail", effectiveDate);
        }

        getWholesalePrices(currencyCode: string, scope: IScope, effectiveDate?: Date): IPriceLookup {
            return this.getSizePrices(currencyCode, scope, "wholesale", effectiveDate);
        }

        getOriginalWholesalePrices(currencyCode: string, scope: IScope, effectiveDate?: Date): IPriceLookup {
            return this.getSizePrices(currencyCode, scope, "original_wholesale", effectiveDate);
        }

        getAdjustedWholesalePrices(currencyCode: string, scope: IScope, effectiveDate?: Date): IPriceLookup {
            return this.getSizePrices(currencyCode, scope, "adjusted_wholesale", effectiveDate);
        }

        getDiscountedWholesalePrices(currencyCode: string, scope: IScope, effectiveDate?: Date): IPriceLookup {
            return this.getSizePrices(currencyCode, scope, "discounted_wholesale", effectiveDate);
        }

        getSizePrices(currencyCode: string, scope: IScope, type: string, effectiveDate?: Date) {
            let prices = {};
            if (scope.brand._id) {
                const usePricesheet =
                    (scope.brand.getPreference("date_based_pricing_enabled") ||
                        scope.brand.getPreference("separate_msrp_currency")) &&
                    (_.size(this.__pricesheets) || _.size(this.__prices)) &&
                    ["wholesale", "retail"].includes(type); // valid type

                const pricingEntry = this.__pricing.get(currencyCode);
                let defaultPrice = 0;
                const priceBackfillIndex = this.__prices?.findIndex(
                    (p) => !p?.__price && p?.__currency_code === currencyCode && p?.__type === type,
                );
                const priceBackfill = priceBackfillIndex > -1 ? this.__prices[priceBackfillIndex] : null;
                if (pricingEntry && !pricingEntry.__disabled && pricingEntry?.[`__${type}`]) {
                    defaultPrice = pricingEntry[`__${type}`];
                }
                if (usePricesheet && priceBackfill && defaultPrice) {
                    priceBackfill.__price = defaultPrice;
                    this.__prices[priceBackfillIndex] = priceBackfill;
                }
                if (usePricesheet) {
                    prices = this.getPricesheetSizePrices(currencyCode, scope, type, effectiveDate);
                } else {
                    prices = this.getPriceTemplateSizePrices(currencyCode, scope, type);
                }
            }
            return prices;
        }

        getSizeGroups(): Array<{ name: string; sizes: ISizeRecord[] }> {
            const map = {};
            this.__sizes?.forEach((size) => {
                const key = `group_${size.__size_group}`; // add string prefix to save the sort (number keys are sorted in JS)
                map[key] ||= { name: size.__size_group || "", sizes: [] };
                map[key].sizes.push(size);
            });
            return Object.values(map);
        }

        // greedily take the first valid price for each size
        getPricesheetSizePrices(currencyCode: string, scope: IScope, type: string, effectiveDate?: Date): IPriceLookup {
            const isPPSEnabled = !!scope.brand.getPreference("price_per_size");
            const pricesheet = this.getPricesheet(currencyCode);
            const buildPriceLookup = initPriceLookupBuilder(scope, type, this.__sizes);

            const pricesheetSizePrices = isUsePricesheet(currencyCode, pricesheet)
                ? buildPricesheetPricing(
                      this,
                      buildPriceLookup,
                      pricesheet.__prices,
                      currencyCode,
                      type,
                      isPPSEnabled,
                      effectiveDate,
                  )
                : buildProductPricing(buildPriceLookup, this.__prices, currencyCode, type, effectiveDate);

            const pricePerSize = scope.brand.hasPreference("price_per_size");
            const pricingEntry = this.__pricing.get(currencyCode);
            let defaultPrice = 0;

            // Calculate the default price if it hasn't already been set and the product is still available
            if (pricingEntry && !pricingEntry.__disabled) {
                defaultPrice = pricingEntry[`__${type}`];
            }

            // After calculating based on price templates and price per size, set any size that hasn't had a
            // price set on it to the calculated defaultPrice
            const discount =
                scope?.brand?.hasPreference("discount_allowed") &&
                scope.company?._id &&
                scope.company?.__settings.__default_discount;
            return fallbackToBasePrices(this.__sizes, pricesheetSizePrices, type, currencyCode, {
                usePricePerSize: pricePerSize,
                defaultPrice,
                discount,
            });
        }

        getPriceTemplateSizePrices(currencyCode, scope, key = "wholesale") {
            if (!scope.brand._id) {
                return [];
            }
            const pricePerSize = scope.brand.hasPreference("price_per_size");
            const sizes = {};
            const pricingEntry = this.__pricing.get(currencyCode);
            let defaultPrice = 0;

            const priceTemplate = this.getPriceTemplate() as any;

            // Calulate the price for the specified priceTemplate, if given
            if (priceTemplate && !priceTemplate.__disabled) {
                const priceTemplateCurrency = _.find(
                    _.get(scope, "brand.settings.pricesheets"),
                    (pricesheet) => pricesheet.name === priceTemplate.__template,
                )?.currency_code;
                if (!priceTemplateCurrency || priceTemplateCurrency === currencyCode) {
                    // in case price_template currency not defined - apply for all
                    defaultPrice = priceTemplate[`__${key}`];
                    if (pricePerSize) {
                        priceTemplate.__sizes.forEach((sizePriceEntry) => {
                            if (!sizes[sizePriceEntry.__size]) {
                                sizes[sizePriceEntry.__size] = sizePriceEntry[`__${key}`];
                            }
                        });
                    }
                }
            }

            // Calculate the default price if it hasn't already been set and the product is still available
            if (!defaultPrice && pricingEntry && !pricingEntry.__disabled) {
                defaultPrice = pricingEntry[`__${key}`];
            }

            // After calculating based on price templates and price per size, set any size that hasn't had a
            // price set on it to the calculated defaultPrice
            const discount =
                scope?.brand?.hasPreference("discount_allowed") &&
                scope.company?._id &&
                scope.company?.__settings.__default_discount;
            return fallbackToBasePrices(this.__sizes, sizes, key, currencyCode, {
                usePricePerSize: pricePerSize,
                defaultPrice,
                discount,
            });
        }

        getAvailableVariants(itemEntities, options) {
            const { warehouses, variants } = options;
            const getVariantsItems = fp.compose(fp.get("items"), fp.find({ key: "color" }));
            return fp.compose(
                fp.filter(
                    ({ ref }: any) =>
                        fp.isEmpty(warehouses) ||
                        (!fp.isEmpty(itemEntities) &&
                            itemEntities[ref]?.__tracks_inventory &&
                            fp.size(fp.intersection(itemEntities[ref].__warehouse_ids, warehouses))),
                ),
            )(_.isEmpty(variants) ? getVariantsItems(this.__variants) : variants);
        }

        getAvailableVariantsItems(itemEntities, options) {
            return fp.compose(
                fp.compact,
                fp.map(({ ref }) => itemEntities[ref]),
            )(this.getAvailableVariants(itemEntities, options));
        }

        getColorVariants() {
            return _.find(this.__variants, { key: "color" })?.items || [];
        }

        hasColorVariants() {
            const colors = _.find(this.__variants, { key: "color" });
            return _.size(colors?.items) > 1 || colors?.hasMore;
        }

        // TODO: Move to redux
        addToOrder(options = {}) {
            (options as any).item_id = this._id;
            return fetch("/api/v2.0/orders/working/line-items", {
                body: JSON.stringify(options),
                method: "POST",
            }).then((response) => {
                if (response.status >= 400) {
                    return response.json().then((data) => {
                        throw new Error(data.message || formatMessage("Error adding to cart"));
                    });
                }
                return response.json();
            });
        }

        removeFromOrder() {
            return fetch(`/api/v2.0/orders/working/line-items?${querystring.stringify({ product: this._id })}`, {
                method: "DELETE",
            }).then((response) => {
                if (response.status >= 400) {
                    return response.json().then((data) => {
                        throw new Error(data.message || formatMessage("Error adding to cart"));
                    });
                }
                return response.json();
            });
        }

        toJS() {
            const js = super.toJS();
            js.__pricing = Object.keys(js.__pricing).map((key) => js.__pricing[key]);
            Object.keys(js).forEach((key) => {
                if (typeof js[key] === "undefined") {
                    delete js[key];
                }
            });
            return js;
        }

        getProductUnavailableMessage() {
            let message = "";
            if (this.__out_of_range) {
                message = formatMessage(
                    "This product is unavailable in this linesheet or custom list's delivery window",
                );
            }

            return message;
        }
    }

    return ItemRecord;
};

const fallbackToBasePrices = (
    itemSizes: List<ISizePrice>,
    sizePrices: IPriceLookup,
    type: string,
    currencyCode: string,
    options: { discount: number; usePricePerSize: boolean; defaultPrice: number },
) => {
    const prices = { ...sizePrices };
    const { usePricePerSize, defaultPrice } = options;
    itemSizes.forEach((sizeEntry) => {
        if (!prices[sizeEntry.__size]) {
            prices[sizeEntry.__size] = usePricePerSize
                ? (sizeEntry.__pricing.get(currencyCode) || {})["__" + type] || defaultPrice
                : defaultPrice;
            // Apply discount here
            if (options.discount && type === "wholesale") {
                prices[sizeEntry.__size] = (prices[sizeEntry.__size] * (100 - options.discount)) / 100;
            }
        }
    });
    return prices;
};

export const withVideoSize = (url: string, size: ImageSize) => {
    switch (size) {
        case "T":
            return `${url}?video=low`;
        case "M":
            return `${url}?video=standard`;
        default:
            return `${url}?video=high`;
    }
};

export const withImageSize = (url: string, size: ImageSize, quality: number = 1) => {
    // correct sizes listed at appserver/legacy/services/AssetService/UploadedAssetMethods.js
    switch (size) {
        case "T":
            return `${url}?max_dimension=${_.ceil(149 * quality)}`;
        case "G":
            return `${url}?max_dimension=${_.ceil(272 * quality)}`;
        case "M":
            return `${url}?max_dimension=${_.ceil(389 * quality)}`;
        case "W":
            return `${url}?max_dimension=2000`;
        default:
            return `${url}?max_dimension=4000`;
    }
};

// validates sizes to avoid invalid dimensions
export const cdnImageSize = (size: ImageSize) => {
    switch (size) {
        case "T":
        case "G":
        case "M":
            return size;
        default:
            return "";
    }
};

export const formatCdn = (imageId: string = "") => {
    return imageId ? `//cdn${(imageId.charCodeAt(imageId.length - 1) % 4) + 1}.nuorder.com` : "//cdn1.nuorder.com";
};

export const getFallbackImage = (scope: IScope, cdn: string = ""): string | undefined => {
    return scope.brand?.settings?.missing_logo_image
        ? `${cdn || formatCdn()}/brands/${scope.brand.settings.missing_logo_image}.jpg`
        : undefined;
};

const parseMedia = _.curry((scope: IScope, size: ImageSize, asset: IProductMediaAsset): IProductMedia => {
    const { type, url } = asset;
    const quality = _.clamp(scope.brand?.preferences.get?.("increase_images_quality"), 1, 3) || 1;
    switch (type) {
        case MEDIA_MAP[MEDIA_TYPE_IMAGE]:
            return {
                type: MEDIA_TYPE_IMAGE as IMAGE,
                source: withImageSize(url, size, quality),
                download: !scope.brand?.hasPreference?.("restrict_image_download")
                    ? `${url}?max_dimension=4000&download=true`
                    : null,
            };
        case MEDIA_MAP[MEDIA_TYPE_360]:
            return {
                type: MEDIA_TYPE_360 as IMAGE_360,
                source: withImageSize(url, size, quality),
                manifest: _.replace(url, "/serve", "/manifest"),
            };
        case MEDIA_MAP[MEDIA_TYPE_VIDEO]:
            return {
                type: MEDIA_TYPE_VIDEO as VIDEO,
                source: withVideoSize(url, size),
                poster: withImageSize(url, size, quality),
            };
        case MEDIA_MAP[MEDIA_TYPE_THREEDIUM]:
            return {
                status: "",
                type: MEDIA_TYPE_THREEDIUM as THREEDIUM,
                source: url,
                threedium_id: asset.threedium_id,
            };
    }
});

// initPriceLookupBuilder :: (Scope, String, Size[]) -> Function -> (PriceLookup, Price) -> PriceLookup
//   ultimately returns a reducer function to build price lookup for either pricesheet or product pricing
//   1) provide common args and build size map
//   2) provide either pricesheet or product price validator
//   3) reduce prices into price lookup
const initPriceLookupBuilder: IInitPriceBuilder = (scope, type, sizes) => {
    const sizeMap = createSizeMap(sizes);

    return (isInvalid) => (priceLookup, price) => {
        const sizeName = get(sizeMap, price.__sku_id);

        return get(priceLookup, [sizeName]) || isInvalid(price)
            ? priceLookup
            : set(priceLookup, [sizeName], calculatePrice(scope, type, price.__price));
    };
};

const calculatePrice = (scope: IScope, type: string, price: number): number =>
    isDiscounted(scope, type) ? calculateDiscount(scope, price) : price;

const buildPricesheetPricing: IBuildPricesheetLookup = (
    item,
    buildPriceLookup,
    pricesheetPrices,
    currencyCode,
    type,
    isPPSEnabled,
    effectiveDate,
) => {
    const isInvalid = createPricesheetPriceValidator(type, effectiveDate);
    const priceLookup = pricesheetPrices.reduce(buildPriceLookup(isInvalid), {});

    // try fallback to any of pricesheet prices in case of non-PPS brand
    const fallbackPrice = _.find(_.values(priceLookup), (price) => price > 0);
    if (!isPPSEnabled && missingPrices(priceLookup, item.__sizes) && !!fallbackPrice) {
        item.__sizes?.forEach((sizeObject) => {
            const sizeName = sizeObject.get("__size");
            priceLookup[sizeName] = priceLookup[sizeName] || fallbackPrice;
        });
    }

    return missingPrices(priceLookup, item.__sizes)
        ? buildProductPricing(buildPriceLookup, item.__prices, currencyCode, type, effectiveDate, priceLookup)
        : priceLookup;
};

const buildProductPricing: IBuildProductLookup = (
    buildPriceLookup,
    productPrices,
    currencyCode,
    type,
    effectiveDate,
    priceLookup = {},
) => {
    const isInvalid = createProductPriceValidator(currencyCode, type, effectiveDate);
    return productPrices.reduce(buildPriceLookup(isInvalid), priceLookup);
};

const missingPrices = (priceLookup: IPriceLookup, sizes: List<any>): boolean =>
    sizes.some((size) => !get(priceLookup, [size.get("__size")]));

const isDiscounted = (scope: IScope, type: string): boolean =>
    scope?.brand?.hasPreference("discount_allowed") &&
    scope?.company?._id &&
    scope?.company?.__settings.__default_discount &&
    type === "wholesale";

const calculateDiscount = (scope: IScope, price: number): number =>
    (price * (100 - scope.company.__settings.__default_discount)) / 100;

const createSizeMap = (sizes: ISizes): ISizeMap => sizes.reduce(updateSizeMap, {});

const updateSizeMap = (sizeMap: ISizeMap, size: ImmutableMap<string, any>): ISizeMap =>
    set(sizeMap, size.get("_id"), size.get("__size"));

const isUsePricesheet = (currencyCode: string, pricesheet: IPricesheet): boolean =>
    !!pricesheet && (!pricesheet.__currency_code || pricesheet.__currency_code === currencyCode);

const getPricesheetValidations = (type: string, effectiveDate?): IPriceValidator[] => [
    typeEq(type),
    isActivePrice(effectiveDate),
];

const getProductValidations = (currencyCode: string, type: string, effectiveDate?): IPriceValidator[] => [
    currencyCodeEq(currencyCode),
    typeEq(type),
    isActivePrice(effectiveDate),
];

const combineValidations: ICombineValidations = (validations) => (price) =>
    validations.some((isValid) => !isValid(price));

const createPricesheetPriceValidator: ICreatePricesheetValidator = pipe(getPricesheetValidations, combineValidations);

const createProductPriceValidator: ICreateProductValidator = pipe(getProductValidations, combineValidations);

// takes an effective date and returns a price validator that determines
// if the effective date is within a price's start/end date.
const isActivePrice = (effectiveDate?): IPriceValidator => {
    const startOfEffectiveDate = startOfDay(effectiveDate);

    return allPass([startsOnOrBefore(startOfEffectiveDate), endsOnOrAfter(startOfEffectiveDate)]);
};

const startsOnOrBefore =
    (effectiveDate: moment.Moment): IPriceValidator =>
    ({ __start_date }) =>
        startOfDay(__start_date || effectiveDate).isSameOrBefore(effectiveDate);

const endsOnOrAfter =
    (effectiveDate: moment.Moment): IPriceValidator =>
    ({ __end_date }) =>
        startOfDay(__end_date || effectiveDate).isSameOrAfter(effectiveDate);

const startOfDay = (date?): moment.Moment => getMomentUTC(date).startOf("day");

// defaults to today
const getMomentUTC = (date?): moment.Moment => (date ? dateToMomentUTC(date) : moment.utc());

const dateToMomentUTC = (date): moment.Moment => {
    const momentDate = moment.utc(date);

    return momentDate.isValid() ? momentDate : moment.utc();
};

const currencyCodeEq: IPricePropValidator = propEq("__currency_code");

const typeEq: IPricePropValidator = propEq("__type");

interface IPriceTemplate {
    __template: string;
    [k: string]: any;
}
interface IPriceLookup {
    [sizeName: string]: number;
}

interface ISizeMap {
    [skuId: string]: string;
}

interface ISizePrice {
    __pricing: Map<
        string,
        {
            _id: string;
            __currency_code: string;
            __wholesale: number;
            __retail: number;
        }
    >;
    __size: string;
    __size_group: string;
    _id: string;
    __weight: number;
    upc: string;
}

interface IPrice {
    _id: string;
    __sku_id: string;
    __currency_code?: string;
    __type: string;
    __price: number;
    __start_date?: any;
    __end_date?: any;
}

interface IPricesheet {
    _id: string;
    __name: string;
    __currency_code?: string;
    __prices: IPrice[];
}

type ISizes = List<ImmutableMap<string, any>>;

type IBuildPricesheetLookup = (
    item,
    buildPriceLookup: IBuildPriceLookup,
    pricesheetPrices: IPrice[],
    currencyCode: string,
    type: string,
    isPPSEnabled: boolean,
    effectiveDate?: any,
) => IPriceLookup;

type IBuildProductLookup = (
    buildPriceLookup: IBuildPriceLookup,
    productPrices: IPrice[],
    currencyCode: string,
    type: string,
    effectiveDate?,
    priceLookup?: IPriceLookup,
) => IPriceLookup;

type IPriceLookupBuilder = (priceLookup: IPriceLookup, price: IPrice) => IPriceLookup;

type IBuildPriceLookup = (isInvalid: IPriceValidator) => IPriceLookupBuilder;

type IInitPriceBuilder = (scope: IScope, type: string, sizes: List<any>) => IBuildPriceLookup;

type IPricePropValidator = (key: string) => IPriceValidator;

type IPriceValidator = (price: IPrice) => boolean;

type ICreatePricesheetValidator = (type: string, effectiveDate?) => IPriceValidator;

type ICreateProductValidator = (currencyCode: string, type: string, effectiveDate?) => IPriceValidator;

type ICombineValidations = (validations: IPriceValidator[]) => IPriceValidator;
